// ABOUTME: Job-level facts of the job detail: state, trigger, who launched or retried it, when, and
// ABOUTME: a skipped concurrency check, why a scheduled run was skipped or that a cancellation is pending.

import { format } from 'date-fns';
import { useIntl } from 'react-intl';

import { useJobSkipReasonLabel, useJobTriggerLabel } from '../../../registry';
import { Jobs } from '../../../types';
import { formatDuration } from '../../../utils/duration';
import { JobStateLabel } from '../../JobStateLabel';
import { InfoList } from './InfoList';

const formatDate = (value: string | null) =>
    value ? format(new Date(value), 'dd/MM/yyyy HH:mm:ss') : '';

export function JobSummary(props: Readonly<{ job: Jobs }>) {
    const { job } = props;
    const intl = useIntl();
    const triggerLabel = useJobTriggerLabel();
    const skipReasonLabel = useJobSkipReasonLabel();

    return (
        <InfoList
            entries={[
                {
                    key: 'state',
                    label: intl.formatMessage({ id: 'job.state', defaultMessage: 'State' }),
                    value: <JobStateLabel state={job.state} />,
                },
                {
                    key: 'skipReason',
                    label: intl.formatMessage({
                        id: 'job.skipReason',
                        defaultMessage: 'Skipped because',
                    }),
                    value: job.skipReason ? skipReasonLabel(job.skipReason) : '',
                },
                {
                    key: 'trigger',
                    label: intl.formatMessage({ id: 'job.trigger', defaultMessage: 'Trigger' }),
                    value: triggerLabel(job.trigger),
                },
                {
                    key: 'user',
                    label: intl.formatMessage({ id: 'job.username', defaultMessage: 'User' }),
                    value: job.createdBy?.username ?? job.username ?? '',
                },
                {
                    key: 'retriedBy',
                    label: intl.formatMessage({
                        id: 'job.retriedBy',
                        defaultMessage: 'Retried by',
                    }),
                    value: job.retriedBy ?? '',
                },
                {
                    key: 'createdAt',
                    label: intl.formatMessage({
                        id: 'job.created_at',
                        defaultMessage: 'Created At',
                    }),
                    value: formatDate(job.createdAt),
                },
                {
                    key: 'scheduledFor',
                    label: intl.formatMessage({
                        id: 'job.scheduledFor',
                        defaultMessage: 'Scheduled for',
                    }),
                    value: formatDate(job.scheduledFor),
                },
                {
                    key: 'skipConcurrencyCheck',
                    label: intl.formatMessage({
                        id: 'job.skipConcurrencyCheck.detail',
                        defaultMessage: 'Concurrency limit',
                    }),
                    value: job.skipConcurrencyCheck
                        ? intl.formatMessage({
                              id: 'job.skipConcurrencyCheck.ignored',
                              defaultMessage: 'Ignored',
                          })
                        : '',
                },
                {
                    key: 'duration',
                    label: intl.formatMessage({ id: 'job.duration', defaultMessage: 'Duration' }),
                    value: formatDuration(job.startedAt, job.endedAt),
                },
                {
                    key: 'cancelRequestedAt',
                    label: intl.formatMessage({
                        id: 'job.cancelRequestedAt',
                        defaultMessage: 'Cancellation requested',
                    }),
                    value: formatDate(job.cancelRequestedAt),
                },
            ]}
        />
    );
}
